import React, { Dispatch, SetStateAction, useContext, useRef, useState } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import { BookmarkBorder, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { getMediaLogo } from '../../utils/file';
import { useTranslation } from 'react-i18next';
import DoneIcon from '@mui/icons-material/Done';
import imgPlaceholder from '../../assets/image-placeholder.svg';
import { Pagination } from 'swiper/modules';
import { CampaignContext } from '../../contexts/CampaignContext';
import usePlatformCardColors from '../../hooks/usePlatformCardColors';

interface MediaFile {
	file: File;
	type: 'image' | 'video';
}

let themesInstagramStory = ['theme1.jpg', 'theme2.jpg', 'theme3.jpg'];

interface InstagramPostProps {
	medias: MediaFile[];
	caption: string;
	staticContent?: boolean;
	multiple?: boolean;
	hashtag?: string;
	contentReady?: boolean;
	preview?: boolean;
	skleton?: boolean;
	data?:
		| {
				image: File | string;
				caption: string;
				type: string;
				dataType: string;
		  }[]
		| null;
	setData?: Dispatch<SetStateAction<{ image: File | string; caption: string; type: string; dataType: string }[]>>;
}

const InstagramPost: React.FC<InstagramPostProps> = ({ contentReady = true, skleton = false, medias, setData, caption, multiple = true, data, hashtag = '', staticContent = false, preview = false }) => {
	const swiperRef = useRef<any>(null);
	const [activeIndex, setActiveIndex] = useState<number>(0);
	const { selectedContentType, selectedPlatform, templateImage, setTemplateImage } = useContext(CampaignContext);
	const { borderColorClass } = usePlatformCardColors(selectedPlatform!);
	const { t } = useTranslation();

	const handleSlideChange = (swiper: any) => {
		setActiveIndex(swiper.activeIndex);
	};

	const isPost = selectedContentType === 'post';
	const serial_Story = !isPost && selectedPlatform === 'influenceral';
	const handleSetTemplateImage = () => {
		if (setData) {
			themesInstagramStory.map((item, index) => {
				if (activeIndex === index && !templateImage.includes(item)) {
					if (serial_Story) {
						setTemplateImage({ image: item, caption, type: 'image', dataType: 'templateImage' });
						setData(prevData => [...prevData, { image: item, caption, type: 'image', dataType: 'templateImage' }]);
					} else {
						setData([{ image: item, caption, type: 'image', dataType: 'templateImage' }]);
						setTemplateImage([{ image: item, caption, type: 'image', dataType: 'templateImage' }]);
					}
				}
			});
		}
	};

	const goToNextSlide = () => {
		if (swiperRef.current) {
			swiperRef.current.swiper.slideNext();
		}
	};

	const goToPrevSlide = () => {
		if (swiperRef.current) {
			swiperRef.current.swiper.slidePrev();
		}
	};

	return (
		<Box className={`relative rounded-2xl border ${borderColorClass}`}>
			<Box className="flex items-center justify-between px-3 py-2">
				<Typography className="text-base font-medium text-grey-900">{selectedContentType ? t(selectedContentType) : t('post')}</Typography>
				<Divider className={`mx-2 grow border ${borderColorClass}`} />
				<img src={getMediaLogo(selectedPlatform!)} alt={`${t(selectedPlatform!)} logo`} className="mb-1 h-8 w-8" />
			</Box>
			{multiple && <Divider className={`border-1 ${borderColorClass}`} />}
			<div className="relative">
				{/* need component */}
				{medias.length > 0 && (
					<div className="absolute top-3 z-20 flex w-full justify-center gap-2 px-3">
						{Array(medias.length)
							.fill(2)
							.map((item, index) => (
								<span key={item} className={`h-1 w-full rounded-sm ${activeIndex === index ? 'bg-white' : 'bg-gray-400'}`}></span>
							))}
					</div>
				)}
				{medias.length > 0 && data && data.length > 0 && <div className="absolute bottom-10 z-20 flex w-full justify-center">{serial_Story ? <p className="break-all rounded-md bg-white p-2 py-4">{t(data[activeIndex].caption)}</p> : <p className="break-all rounded-md bg-white p-2 py-4">{t(caption)}</p>}</div>}
				{skleton && medias?.length === 0 && !staticContent && <img src={imgPlaceholder} className={`${!isPost ? 'h-96 w-64 rounded-b-2xl' : 'h-64 w-64'} w-full object-cover`} alt="placeholder" />}
				{
					<Swiper ref={swiperRef} slidesPerView={1} pagination={true} modules={[Pagination]} className="mySwiper" onSlideChange={handleSlideChange}>
						{medias.slice(0, 10)?.map((media, index) => (
							<SwiperSlide key={index}>
								<div className={`relative`}>{media.type === 'image' ? <img src={URL.createObjectURL(media.file)} alt={`Post ${index + 1}`} className={`${!isPost ? 'h-96 w-64 rounded-b-2xl' : 'h-64 w-64'} w-full object-cover`} /> : <video src={URL.createObjectURL(media.file)} className="h-64 w-full object-cover" controls />}</div>
							</SwiperSlide>
						))}
					</Swiper>
				}

				{isPost && staticContent && medias.length <= 0 ? (
					<Box className="relative flex justify-center">
						<img src={'/images/campaigns/background_instagram_post.jpg'} className={`w-full object-cover brightness-110`} alt="placeholder" />
						<Typography className="absolute top-1/2">{t('photo_your_post')}</Typography>
					</Box>
				) : (
					!isPost &&
					medias.length <= 0 &&
					staticContent && (
						<Box className="relative flex w-full flex-col items-center p-5">
							<div className="">
								<Button variant="text" onClick={goToNextSlide} className="absolute -left-3 top-1/2">
									<KeyboardArrowLeft />
								</Button>
								<Button variant="text" onClick={goToPrevSlide} className="absolute -right-3 top-1/2">
									<KeyboardArrowRight />
								</Button>
							</div>
							<Swiper className={`w-full transition-all`} pagination={{ clickable: true }} onSlideChange={handleSlideChange} ref={swiperRef} modules={[Pagination]}>
								{themesInstagramStory.map((item, index) => (
									<SwiperSlide key={index}>
										<img src={`/images/campaigns/${item}`} className={`h-96 w-full rounded-b-2xl object-contain`} alt="placeholder" />
									</SwiperSlide>
								))}
							</Swiper>
							{contentReady && (
								<>
									<Button className="w-11/12" onClick={handleSetTemplateImage}>
										{data && data?.length > 0 && data?.find(item => item.image === themesInstagramStory[activeIndex])?.image === themesInstagramStory[activeIndex] ? <DoneIcon /> : t('select')}
									</Button>
									<Box className="absolute bottom-0 flex justify-center">
										{Array(themesInstagramStory.length)
											.fill(2)
											.map((item, index) => (
												<span key={item} className={`mx-1 h-2.5 w-2.5 rounded-full ${activeIndex === index ? 'bg-blue-500' : 'bg-blue-200'}`}></span>
											))}
									</Box>
								</>
							)}
						</Box>
					)
				)}
			</div>
			{/* need component */}
			{!isPost ? (
				preview && (
					<div className="">
						<div className="mx-4 my-2 flex items-center justify-between">
							<BookmarkBorder className="h-4 w-4 text-grey-800" />
							{!isPost && !staticContent && <Box className="mr-10 flex justify-center gap-x-1">{medias?.map((_, index) => <span key={index} className={`h-1 w-1 rounded-full ${activeIndex === index ? 'bg-primary-main' : 'bg-grey-600'}`} />)}</Box>}
							<div className="flex gap-x-4">
								<img src="/images/campaigns/Outline.png" className="h-4 w-4 object-cover" alt="" />
								<img src="/images/campaigns/CommentIcon.png" className="h-4 w-4 object-cover" alt="" />
								<img src="/images/campaigns/HeartIcon.png" className="h-4 w-4 object-cover" alt="" />
							</div>
						</div>
						<Box className="flex flex-col items-end p-5 pt-0 text-text-primary">
							<Typography className="break-all py-4 text-justify text-xs font-medium">{t(caption)}</Typography>
							<Typography className="break-all text-justify text-xs font-medium">{t(hashtag)}</Typography>
						</Box>
					</div>
				)
			) : (
				<>
					{selectedPlatform === 'instagram' ? (
						<>
							<div className="mx-4 my-2 flex items-center justify-between">
								<BookmarkBorder className="h-4 w-4 text-grey-800" />
								{!isPost && !staticContent && <Box className="mr-10 flex justify-center gap-x-1">{medias?.map((_, index) => <span key={index} className={`h-1 w-1 rounded-full ${activeIndex === index ? 'bg-primary-main' : 'bg-grey-600'}`} />)}</Box>}
								<div className="flex gap-x-4">
									<img src="/images/campaigns/Outline.png" className="h-4 w-4 object-cover" alt="" />
									<img src="/images/campaigns/CommentIcon.png" className="h-4 w-4 object-cover" alt="" />
									<img src="/images/campaigns/HeartIcon.png" className="h-4 w-4 object-cover" alt="" />
								</div>
							</div>
							<Box className="flex flex-col items-end p-5 pt-0 text-text-primary">
								<Typography className="break-all py-5 text-justify text-xs font-medium">{t(caption)}</Typography>
								<Typography className="break-all text-justify text-xs font-medium">{t(hashtag)}</Typography>
							</Box>
						</>
					) : (
						<>
							<Box className="relative my-5 flex flex-col items-end p-5 text-text-primary">
								<Typography className={`break-all text-justify ${caption.length <= 0 ? 'h-4' : 'h-auto'} text-xs font-medium`}>{t(caption)}</Typography>
								<Typography className="mb-16 break-all text-justify text-xs font-medium">{t(hashtag)}</Typography>
								<Box className="absolute bottom-12 flex">
									<img src="/images/campaigns/telegram_reaction1.png" className="mx-1 object-contain" alt="" />
									<img src="/images/campaigns/telegram_reaction2.png" className="mx-1 object-contain" alt="" />
								</Box>
								<img className="absolute bottom-0 left-0 right-0 object-cover" src="/images/campaigns/telegram_menu.png" alt="menu_telegram" />
							</Box>
						</>
					)}
				</>
			)}
		</Box>
	);
};

export default InstagramPost;
